import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'

import { useUnit } from 'effector-react'
import { debounce } from 'lodash'

import { $fanId } from '@/models/chattingUI/chats/chats.ts'
import {
  $canLoadNextMessagePages,
  $canLoadPrevMessagePages,
  $isNextPageFetchingError,
  $isPreviousPageFetchingError,
  $messages,
  getMessagesFx,
  getNextMessagePageFx,
  getPreviousMessagePageFx,
} from '@/models/chattingUI/messages/messages.ts'

export const useChatMessagesScrollListener = ({
  messagesContainerRef,
}: {
  messagesContainerRef: React.RefObject<HTMLDivElement>
}) => {
  const fanId = useUnit($fanId)
  const messages = useUnit($messages)
  const isFetchingPageMessages = useUnit(getMessagesFx.pending)
  const isNextPageFetchingError = useUnit($isNextPageFetchingError)
  const isPreviousPageFetchingError = useUnit($isPreviousPageFetchingError)
  const previousScrollHeightRef = useRef(0)

  const highlightMessage = useCallback((messageId: number) => {
    const element = document.getElementById(`chatting-message-${messageId}`)

    if (element) {
      element.classList.remove('animate-pulse', 'repeat-1')
      element.scrollIntoView()
      element.classList.add('animate-pulse', 'repeat-1')
    }
  }, [])

  const canLoadNextMessagePages = useUnit($canLoadNextMessagePages)
  const canLoadPrevMessagePages = useUnit($canLoadPrevMessagePages)

  useEffect(() => {
    const messageContainer = messagesContainerRef.current

    if (!messageContainer || isFetchingPageMessages || !fanId) {
      return
    }

    const canLoadPrev = canLoadPrevMessagePages && !isPreviousPageFetchingError
    const canLoadNext = canLoadNextMessagePages && !isNextPageFetchingError

    const scrollListener = (event: Event) => {
      const target = event.target as HTMLElement

      if (!target) return

      const { scrollTop, scrollHeight, clientHeight } = target

      if (scrollTop < 200 && canLoadNext) {
        previousScrollHeightRef.current = scrollHeight
        getNextMessagePageFx()
      }

      if (scrollHeight - scrollTop - clientHeight < 200 && canLoadPrev) {
        previousScrollHeightRef.current = scrollHeight
        getPreviousMessagePageFx()
      }
    }

    if (messageContainer.clientHeight === messageContainer.scrollHeight) {
      previousScrollHeightRef.current = 0
      if (canLoadNext) {
        getNextMessagePageFx()
      } else if (canLoadPrev) {
        getPreviousMessagePageFx()
      }
    }

    const debouncedScrollListener = debounce(scrollListener, 100)

    messageContainer.addEventListener('scroll', debouncedScrollListener)

    return () => {
      messageContainer.removeEventListener('scroll', debouncedScrollListener)
    }
  }, [
    isFetchingPageMessages,
    messagesContainerRef,
    isNextPageFetchingError,
    isPreviousPageFetchingError,
    canLoadPrevMessagePages,
    canLoadNextMessagePages,
    fanId,
  ])

  useLayoutEffect(() => {
    const messageContainer = messagesContainerRef.current

    if (!messageContainer) {
      return
    }

    const previousScrollHeight = previousScrollHeightRef.current

    if (previousScrollHeight === 0) {
      previousScrollHeightRef.current = messageContainer.scrollHeight

      return
    }

    const newScrollHeight = messageContainer.scrollHeight
    const scrollPositionChange = newScrollHeight - previousScrollHeight

    if (messageContainer.scrollTop < 30 && scrollPositionChange > 0) {
      messageContainer.scrollTop += scrollPositionChange
    }

    previousScrollHeightRef.current = newScrollHeight
  }, [messagesContainerRef, messages])

  return {
    highlightMessage,
  }
}
